import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, XCircle, ChevronDown, ChevronUp, Pencil, Save, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const AGE_RANGES = ["6-8", "9-11", "12-14", "15-17", "18+"];
const GRADE_BANDS = ["Elementary", "Middle School", "High School", "Adult"];

const AdminStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [messaging, setMessaging] = useState(null);

  useEffect(() => { loadStudents(); }, []);

  const messageStudent = async (student) => {
    setMessaging(student.id);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/messages/conversations`,
        { recipient_id: student.id, recipient_role: "student" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate("/admin/messages", { state: { openConversationId: res.data.id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open conversation");
    } finally {
      setMessaging(null);
    }
  };

  const startEdit = (s) => {
    setExpanded(null);
    setEditTarget(s.id);
    setEditForm({
      name: s.name || "",
      email: s.email || "",
      age_range: s.age_range || "",
      grade_band: s.grade_band || "",
      guardian_email: s.guardian_email || ""
    });
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editForm.name?.trim() || !editForm.email?.trim()) {
      toast.error("Name and email cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.put(`${API}/students/${editTarget}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(prev => prev.map(s => (s.id === editTarget ? response.data : s)));
      toast.success("Student details updated");
      cancelEdit();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update student");
    } finally {
      setSaving(false);
    }
  };

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/students`, { headers: { Authorization: `Bearer ${token}` } });
      setStudents(response.data);
    } catch (error) {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async (studentId) => {
    if (expanded === studentId) {
      setExpanded(null);
      return;
    }
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/enrollments/student/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(prev => ({ ...prev, [studentId]: response.data }));
      setExpanded(studentId);
    } catch (error) {
      // If endpoint doesn't exist, just expand empty
      setEnrollments(prev => ({ ...prev, [studentId]: [] }));
      setExpanded(studentId);
    }
  };

  const deleteStudent = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/students/${deleteTarget}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Student deleted");
      setDeleteTarget(null);
      loadStudents();
    } catch (error) {
      toast.error("Failed to delete student");
    }
  };

  const cancelEnrollment = async () => {
    if (!cancelTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/enrollments/${cancelTarget.enrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Enrollment cancelled");
      setCancelTarget(null);
      // Reload that student's enrollments
      if (cancelTarget.studentId) {
        const response = await axios.get(`${API}/enrollments/student/${cancelTarget.studentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnrollments(prev => ({ ...prev, [cancelTarget.studentId]: response.data }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2" data-testid="students-title">Students</h1>
      <p className="text-[#4A4A4A] mb-8">Students and their enrollments</p>

      {students.length === 0 ? (
        <div className="brutalist-card p-12 text-center"><p className="text-[#4A4A4A]">No students have registered yet.</p></div>
      ) : (
        <div className="space-y-3">
          {students.map(s => (
            <div key={s.id} className="brutalist-card overflow-hidden" data-testid={`student-card-${s.id}`}>
              {editTarget === s.id ? (
                <div className="p-4 space-y-3" data-testid={`student-edit-form-${s.id}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Name</label>
                      <input
                        data-testid={`edit-name-${s.id}`}
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="brutalist-input w-full mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Email</label>
                      <input
                        data-testid={`edit-email-${s.id}`}
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="brutalist-input w-full mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Age Range</label>
                      <select
                        data-testid={`edit-age-range-${s.id}`}
                        value={editForm.age_range}
                        onChange={(e) => setEditForm({ ...editForm, age_range: e.target.value })}
                        className="brutalist-input w-full mt-1"
                      >
                        <option value="">Select...</option>
                        {AGE_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Grade Band</label>
                      <select
                        data-testid={`edit-grade-band-${s.id}`}
                        value={editForm.grade_band}
                        onChange={(e) => setEditForm({ ...editForm, grade_band: e.target.value })}
                        className="brutalist-input w-full mt-1"
                      >
                        <option value="">Select...</option>
                        {GRADE_BANDS.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Guardian Email</label>
                      <input
                        data-testid={`edit-guardian-email-${s.id}`}
                        value={editForm.guardian_email}
                        onChange={(e) => setEditForm({ ...editForm, guardian_email: e.target.value })}
                        className="brutalist-input w-full mt-1"
                        placeholder="Required if under 18"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={cancelEdit}
                      disabled={saving}
                      className="px-3 py-2 border-2 border-[#0A0A0A] rounded-lg font-bold text-sm flex items-center gap-1 hover:bg-[#F0EFEB]"
                      data-testid={`cancel-edit-student-${s.id}`}
                    >
                      <X className="w-4 h-4" /> Cancel
                    </button>
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="brutalist-btn-primary px-3 py-2 text-sm flex items-center gap-1"
                      data-testid={`save-edit-student-${s.id}`}
                    >
                      <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              ) : (
              <div className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#A0D2F8] border-2 border-[#0A0A0A] flex items-center justify-center text-lg font-bold">
                  {s.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">{s.name}</h3>
                  <p className="text-sm text-[#4A4A4A]">{s.email}</p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {s.age_range && s.age_range !== "18+" && <span className="text-xs bg-[#FBE7C6] px-2 py-0.5 rounded-full border border-[#0A0A0A] font-bold">Minor</span>}
                    {s.guardian_email && <span className="text-xs text-[#4A4A4A]">Guardian: {s.guardian_email}</span>}
                  </div>
                </div>
                <button onClick={() => startEdit(s)} className="p-2 hover:bg-[#F0EFEB] rounded" data-testid={`edit-student-${s.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => messageStudent(s)}
                  disabled={messaging === s.id}
                  className="p-2 hover:bg-[#F0EFEB] rounded"
                  title="Message this student"
                  data-testid={`message-student-${s.id}`}
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button onClick={() => loadEnrollments(s.id)} className="p-2 hover:bg-[#F0EFEB] rounded" data-testid={`expand-student-${s.id}`}>
                  {expanded === s.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <button onClick={() => setDeleteTarget(s.id)} className="p-2 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded" data-testid={`delete-student-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              )}

              {expanded === s.id && (
                <div className="border-t-2 border-[#0A0A0A]/10 px-4 py-3 bg-[#F0EFEB]/50">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2">Enrollments</div>
                  {(enrollments[s.id] || []).length === 0 ? (
                    <p className="text-sm text-[#4A4A4A] italic">No active enrollments</p>
                  ) : (
                    <div className="space-y-2">
                      {(enrollments[s.id] || []).map(e => (
                        <div key={e.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#0A0A0A]/10" data-testid={`enrollment-${e.id}`}>
                          <span className="text-xl">{e.language_flag}</span>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{e.course_name}</div>
                            <div className="text-xs text-[#4A4A4A]">
                              {e.tutor_name && `${e.tutor_name} · `}
                              {e.scheduled_day !== null && `${DAY_NAMES[e.scheduled_day]}s at ${e.scheduled_time} · `}
                              Week {e.current_week}/{e.duration_weeks}
                            </div>
                          </div>
                          <button
                            onClick={() => setCancelTarget({ enrollmentId: e.id, studentId: s.id })}
                            className="text-xs px-2 py-1 border border-[#FF6B6B] text-[#FF6B6B] rounded hover:bg-[#FFAEBC] flex items-center gap-1"
                            data-testid={`admin-cancel-enrollment-${e.id}`}
                          >
                            <XCircle className="w-3 h-3" /> Cancel
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Student?"
        description="This will permanently remove this student and all their enrollment data."
        onConfirm={deleteStudent}
        destructive
      />
      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancel Enrollment?"
        description="This will cancel this student's enrollment and remove all their scheduled sessions."
        onConfirm={cancelEnrollment}
        destructive
      />
    </div>
  );
};

export default AdminStudents;
